import axios, {AxiosError} from 'axios';
import { isApp } from '../utils/EnvUtils';
import { JsBridge } from '../utils/JsBridge'; // 从 index.tsx 导入 tokenPromise
import { getApiDomain } from '../config/domain';

// 使用域名配置中的API地址
const _host = getApiDomain();
const _hostTest = 'https://testvq.deerbook.cn';
const _hostname = window.location.hostname.toLowerCase();
const _isBrandHost =
  _hostname === 'deerbook.cn' ||
  _hostname.endsWith('.deerbook.cn') ||
  _hostname === 'huiyabook.cn' ||
  _hostname.endsWith('.huiyabook.cn');
const _nonProdPrefixes = ['test', 'dev', 'sit', 'uat', 'pre', 'staging', 'qa'];
const _firstDomainLabel = _hostname.split('.')[0];
const _isNonProdSubdomain = _nonProdPrefixes.some((prefix) =>
  _firstDomainLabel.startsWith(prefix)
);
const _baseUrl = _isBrandHost && !_isNonProdSubdomain ? _host : _hostTest;
// const _baseUrl = _host;
// const _baseUrl = "http://192.168.31.123:8080/";

console.log('当前的环境:', window.location.host);
console.log('API基础的URL:', _baseUrl);

export function getApiBaseHost(): string {
  return _baseUrl;
}

export function getWebBaseHost(): string {
  return 'https://' + window.location.host;
}

export const axiosInstance = axios.create({
  baseURL: _baseUrl,
});

// 添加请求拦截器来动态设置 token，并确保等待 token
axiosInstance.interceptors.request.use(
  async function (config) {
    let token: string | null = null;

    // 方式1: 从 UserAgent 中获取（App端注入）
    const userAgent = navigator.userAgent;
    const authMatch = userAgent.match(/Authorization_Bearer\s([^\s]+)/);
    if (authMatch && authMatch[1]) {
      token = authMatch[1];
    }

    // 方式2: 从 localStorage 中获取（booking-platform登录后存储）
    if (!token) {
      token = localStorage.getItem('booking_token');
    }

    // 方式3: 从 sessionStorage 中获取（小程序场景）
    if (!token) {
      token = sessionStorage.getItem('booking_token');
    }

    // 方式4: 从 localStorage 中获取通用token（其他模块使用）
    if (!token) {
      token = localStorage.getItem('token');
    }

    // 设置 Authorization header
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    } else {
      // 本地调试：从 localStorage 读取 token
      const localToken = localStorage.getItem('token');
      if (localToken) {
        config.headers.authorization = localToken.startsWith('Bearer ') ? localToken : `Bearer ${localToken}`;
      }
    }

    //mock
    //  config.headers.authorization =
    //  'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOjEwMTI0NzksIm1pZCI6MTYzLCJleHAiOjE3Mzk5NTY1OTV9.OR_ZW-EQ7FHcUoHuqc6bcOd1v0MhGqBuWFURGg7sf2U'
    console.log('请求配置:', {
      url: config.url,
      method: config.method,
      hasToken: !!token,
      params: config.params,
      data: config.data,
      headers: config.headers
    });
    return config;
  },
  function (error) {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

const _nativeDecrypt = (data: string) => {
  return new Promise((resolve) => {
    JsBridge.nativeDecrypt(data, (args: string) => {
      console.log('args', args);
      resolve(args);
    });
  });
};

axiosInstance.interceptors.response.use(
  async function (response) {
    // 生产解密
    if (
      _baseUrl.startsWith(_host) &&
      response?.data?.data &&
      response?.data?.eType === 1
    ) {
      const _decryptData = await _nativeDecrypt(response?.data?.data);
      response.data.data = _decryptData;
    }
    console.log('响应数据:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response;
  },
  function (error) {
    console.error('响应错误:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      response: error.response?.data
    });
    if (error.response?.status === 401) {
      // 判断是否是booking-platform的请求
      const isBookingRequest = error.config?.url?.includes('/v1/student') || 
                               error.config?.url?.includes('/v1/booking');
      
      if (isBookingRequest) {
        // booking-platform请求：跳转到登录页
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/booking/login?redirect=${encodeURIComponent(currentPath)}`;
      } else {
        // 其他请求：使用App登录
        if (isApp()) {
          JsBridge.gotoLogin({ sourceFrom: 'h5' });
        }
      }
      return;
    }
    if (error.response?.status === 400) {
      return error.response;
    }
    return Promise.reject(error);
  }
);

/**
 * 发起 GET 基础请求，包含自己服务端约定格式数据的基本逻辑处理，第三方的请求不要调用该基础请求
 * @template T - 响应数据的类型
 * @param {string} url 请求的 URL
 * @param {Record<string, any>} params 请求的参数
 * @returns {Promise<{success: boolean, data?: T, msg?: string, code?: number}>} 返回一个包含结果的 Promise 对象
 */
export async function requestGet<T>(
  url: string,
  params: Record<string, any>
): Promise<{ success: boolean; data?: T; msg?: string; code?: number }> {
  try {
    const response = await axiosInstance.get(url, { params: params });

    if (response.status === 200 && response.data?.result === 'SUCCESS') {
      return {
        success: true,
        data: response.data.data,
        msg: response.data?.msg ?? '请求成功',
        code: response.status,
      };
    } else {
      return {
        success: false,
        msg: response.data?.msg ?? '请求失败',
        code: response.status,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      // 服务器返回了响应，但状态码不是 2xx
      return {
        success: false,
        msg: error?.response?.data?.msg ?? '请求失败',
        code: error?.response?.status ?? 500,
      };
    } else {
      // 请求已发出，但没有收到响应
      return {
        success: false,
        msg: '网络错误，请检查网络连接',
        code: 500,
      };
    }
  }
}

export async function requestPost<T>(
  url: string,
  data?: any,
  params?: Record<string, any>
): Promise<{ success: boolean; data?: T; msg?: string; code?: number }> {
  try {
    const response = await axiosInstance.post(url, data, { params: params });
    if (response.status === 200 && response.data?.result === 'SUCCESS') {
      return {
        success: true,
        data: response.data.data,
        msg: response.data?.msg ?? '请求成功',
        code: response.status,
      };
    } else {
      return {
        success: false,
        msg: response.data?.msg ?? '请求失败',
        code: response.status,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      // 服务器返回了响应，但状态码不是 2xx
      return {
        success: false,
        msg: error?.response?.data?.msg ?? '请求失败',
        code: error?.response?.status ?? 500,
      };
    } else {
      // 请求已发出，但没有收到响应
      return {
        success: false,
        msg: '网络错误，请检查网络连接',
        code: 500,
      };
    }
  }
}
